import { forwardRef, Fragment, Ref, useMemo } from "react";
import { findWhere } from "underscore";
import { useAtomValue } from "jotai";

import ThumbnailWidget from "../../../Widgets/ThumbnailWidget/ThumbnailWidget";
import { ImageFieldPresentation } from "../../RecordNameCell";
import { tenantIDAtom } from "../../../../atoms/GlobalAtoms";
import { Module } from "../../../../types/TenantInterfaces";
import MultiReferenceTooltip from "../MultiReferenceToolip";
import SingleReferenceRecord from "./SingleReferenceRecord";
import AppTooltip from "../../../../elements/AppTooltip";
import AppSpinner from "../../../../elements/AppSpinner";
import useModule from "../../../../hooks/useModule";
import { IsMobileAtom } from "../../../../App";
import SubitemDisplay from "./SubitemDisplay";
import EmptyField from "../../EmptyField";

interface onView {
  recordModule: Module;
  recordID: string;
  isSubitem?: boolean;
  subitemID?: string;
  subitemSchema?: any;
}

interface MultiReferenceRecordProps {
  onOpen: () => void;
  onView: (data: onView) => void;
  existingValue: Record<string, any>[];
  submitting?: boolean;
  maxValuesDisplayed?: number;
  tableView: boolean;
  metadataField: string | null;
  metadataFieldType: string | null;
}

const MultiReferenceRecord = (
  {
    onOpen,
    onView,
    existingValue,
    submitting,
    maxValuesDisplayed = 5,
    tableView,
    metadataField,
    metadataFieldType,
  }: MultiReferenceRecordProps,
  ref: Ref<HTMLDivElement>
) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const { getModule } = useModule();

  const moreThanMax = existingValue?.length > maxValuesDisplayed;

  const finalReferences = useMemo(() => {
    if (!Array.isArray(existingValue)) return [];

    const slicedValues = moreThanMax
      ? existingValue.slice(0, maxValuesDisplayed)
      : existingValue;

    return slicedValues?.map((value) => {
      const mod = getModule(value?._ModuleAPIName);
      const imageFieldAPIName = mod?.Templates?.RecordDetailView?.ImageField;
      const imageField = mod?.Fields?.find(
        (f) => f.APIName === imageFieldAPIName
      );
      const imageFieldPresentation = imageField?.Presentation ?? null;

      return {
        record: value,
        imageFieldAPIName,
        imageFieldPresentation,
        mod,
      };
    });
  }, [existingValue, moreThanMax]);

  if (submitting || !existingValue || existingValue?.length === 0) {
    return (
      <div ref={ref} className={mobileScreen ? "pr-1" : "pr-4"}>
        {submitting ? (
          <AppSpinner size="sm" />
        ) : (
          <EmptyField table={tableView} />
        )}
      </div>
    );
  }

  return (
    <div ref={ref}>
      {!Array.isArray(finalReferences) ? (
        <EmptyField table={tableView} />
      ) : (
        finalReferences?.map((value, i) => {
          const { record, imageFieldAPIName, imageFieldPresentation, mod } =
            value;
          const isLast = i === finalReferences.length - 1;

          if (!tableView) {
            return (
              <SingleReferenceRecord
                ref={ref}
                onOpen={onOpen}
                onView={() => {
                  onOpen();
                  viewValue(mod!, record);
                }}
                existingValue={record}
                submitting={submitting}
                imageField={imageFieldAPIName}
                imageFieldPresentation={imageFieldPresentation}
                metadataField={metadataField}
                metadataFieldType={metadataFieldType}
                tableView={tableView}
                className={!isLast ? "tw:mb-1" : undefined}
              />
            );
          }

          if (record?._isSubitem) {
            return (
              <Fragment key={record?._SubitemID}>
                <SubitemDisplay
                  module={mod}
                  existingValue={record}
                  ref={undefined}
                  onOpen={onOpen}
                  onView={() => viewValue(mod!, record)}
                />
                {!isLast && <span className="pr-2 text-dark">,</span>}
              </Fragment>
            );
          }

          return (
            <Fragment key={record?.id}>
              <AppTooltip
                content={
                  <MultiReferenceTooltip
                    records={existingValue}
                    startingIndex={i}
                    onView={() => {
                      onOpen();
                      viewValue(mod!, record);
                    }}
                    imageField={imageFieldAPIName}
                    imageFieldPresentation={imageFieldPresentation}
                  />
                }
              >
                <div
                  className={`tw:absolute tw:inline-flex`}
                  style={{ left: i * 22, bottom: -10 }}
                >
                  <ReferenceRecordDisplay
                    padding={false}
                    onOpen={onOpen}
                    onView={() => viewValue(mod!, record)}
                    existingValue={record}
                    module={mod!}
                    moreThanMax={moreThanMax && isLast}
                    imageField={imageFieldAPIName || null}
                    imageFieldPresentation={imageFieldPresentation}
                    tableView={tableView}
                  />
                </div>
              </AppTooltip>
            </Fragment>
          );
        })
      )}
    </div>
  );

  function viewValue(mod: Module, value: Record<string, any>) {
    if (window.location.pathname.startsWith("/share/")) return;

    if (value?._isSubitem) {
      const subitemSchema = findWhere(mod?.Fields, {
        APIName: value?._SubitemFieldAPIName,
      });

      return onView({
        isSubitem: true,
        recordID: value.id,
        recordModule: mod,
        subitemSchema: subitemSchema,
        subitemID: value?._SubitemID,
      });
    }

    onView({
      recordModule: mod,
      recordID: value.id,
    });
  }
};

interface ReferenceRecordDisplayProps {
  padding?: boolean;
  onOpen: () => void;
  onView: () => void;
  existingValue: Record<string, any>;
  module: Module;
  moreThanMax?: boolean;
  imageField: string | null;
  imageFieldPresentation: ImageFieldPresentation;
  tableView: boolean;
}

function ReferenceRecordDisplay({
  padding,
  onOpen,
  onView,
  existingValue,
  moreThanMax,
  imageField,
  imageFieldPresentation,
}: ReferenceRecordDisplayProps) {
  const tenantID = useAtomValue(tenantIDAtom);
  const mobileScreen = useAtomValue(IsMobileAtom);
  const link = `/clouds/${tenantID}/records/${
    existingValue?._ModuleAPIName
  }/details/${existingValue?.id ?? ""}/Summary`;

  return (
    <div
      style={{ maxWidth: 340 }}
      className={`tw:truncate tw:inline-flex tw:items-center ${
        padding ? "tw:pr-4" : mobileScreen ? "tw:pr-1" : ""
      }`}
    >
      <ThumbnailWidget
        record={existingValue}
        imageKey={imageField || ""}
        mode={imageFieldPresentation}
        withBorder={false}
      />
      <a
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          onOpen();
          onView();
        }}
        className="link-primary tw:cursor-pointer"
        href={link}
      >
        {moreThanMax && (
          <span
            className="tw:text-medium"
            style={{
              fontSize: 13,
            }}
          >
            + more
          </span>
        )}
      </a>
    </div>
  );
}

export default forwardRef(MultiReferenceRecord);
